import WolfButton from '@components/ui/WolfButton';
import WolfInput from '@components/ui/WolfInput';
import WolfTooltip from '@components/ui/WolfTooltip';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { Box, Typography, Checkbox, FormControlLabel } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { RootState } from '@redux/store';
import { validEmail } from '@utils/validEmail';
import { SetStateAction, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

interface CreateEditOrganizationModalProps {
  modalClose: () => void;
  createOrganization?: (
    name: string,
    email: string,
    firstName: string,
    lastName: string,
    organizationId?: string,
    description?: string,
    skipSeed?: boolean,
  ) => void;
  createOrganizationByEmail?: (name: string, email: string, description?: string, skipSeed?: boolean) => void;
  editOrganization?: (
    name: string,
    email: string,
    firstName: string,
    lastName: string,
    organizationId: string,
    description?: string,
  ) => void;
  saving: boolean;
  editing?: boolean;
  useSimplifiedCreation?: boolean; // Flag to use simplified creation (email only)
  hideOwnerEmail?: boolean; // Flag to hide owner email field completely
  initialData?: { name: string; organizationId: string; description?: string } | null | undefined;
}

const CreateEditOrganizationModal: React.FC<CreateEditOrganizationModalProps> = ({
  modalClose,
  createOrganization,
  createOrganizationByEmail,
  editOrganization,
  saving,
  editing = false,
  useSimplifiedCreation = false,
  hideOwnerEmail = false,
  initialData,
}) => {
  const localeCommon = useTranslation('common');
  const localeUsers = useTranslation('users');
  const localeActionMessages = useTranslation('actionMessages');
  const localeOrganizations = useTranslation('organizations');
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [organizationDescription, setOrganizationDescription] = useState<string | null>(null);
  const [ownerEmail, setOwnerEmail] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [showDescriptionWarning, setShowDescriptionWarning] = useState(false);
  const [seedData, setSeedData] = useState(true);

  const dispatch = useDispatch();
  const { email: currentUserEmail } = useAppSelector((state: RootState) => state.user);

  const MINIMUM_DESCRIPTION_LENGTH = 100;

  useEffect(() => {
    if (editing && initialData) {
      setOrganizationName(initialData?.name);
      setOrganizationDescription(initialData?.description || '');
    }

    // Auto-populate owner email with current user's email when using simplified creation
    if (useSimplifiedCreation && currentUserEmail && !ownerEmail) {
      setOwnerEmail(currentUserEmail);
    }
  }, [editing, initialData, useSimplifiedCreation, currentUserEmail, ownerEmail]);

  const handleNameChange = (event: { target: { value: SetStateAction<string | null> } }) => {
    setOrganizationName(event.target.value);
  };

  const handleDescriptionChange = (event: { target: { value: SetStateAction<string | null> } }) => {
    setOrganizationDescription(event.target.value);
  };

  const handleEmailChange = (event: { target: { value: SetStateAction<string | null> } }) => {
    setOwnerEmail(event.target.value);
  };

  const handleFirstNameChange = (event: { target: { value: SetStateAction<string | null> } }) => {
    setFirstName(event.target.value);
  };

  const handleLastNameChange = (event: { target: { value: SetStateAction<string | null> } }) => {
    setLastName(event.target.value);
  };

  const proceedWithCreation = () => {
    if (editing && initialData) {
      editOrganization &&
        editOrganization(
          organizationName || '',
          '',
          '',
          '',
          initialData.organizationId || '',
          organizationDescription || '',
        );
    } else {
      // Check if we should use simplified creation (email only) or full creation
      if (useSimplifiedCreation && createOrganizationByEmail) {
        // Use simplified creation - only organization name, description, and owner email
        createOrganizationByEmail(organizationName || '', ownerEmail || '', organizationDescription || '', !seedData);
      } else {
        // Use traditional creation with owner firstName and lastName
        createOrganization &&
          createOrganization(
            organizationName || '',
            ownerEmail || '',
            firstName || '',
            lastName || '',
            '',
            organizationDescription || '',
            !seedData,
          );
      }
    }
    modalClose();
  };

  const submit = () => {
    // Different validation based on creation type
    if (editing) {
      if (!organizationName) {
        dispatch(
          appendActionMessage({ message: localeActionMessages['invalidOrganizationData'], type: SnackType.ERROR }),
        );
        return;
      }
    } else {
      // Creation mode - different validation based on simplified vs full creation
      if (useSimplifiedCreation) {
        // Simplified creation - only need organization name and owner email
        if (!organizationName || !ownerEmail || !validEmail(ownerEmail)) {
          dispatch(
            appendActionMessage({ message: localeActionMessages['invalidOrganizationData'], type: SnackType.ERROR }),
          );
          return;
        }
      } else {
        // Full creation - need all fields
        if (!organizationName || !ownerEmail || !firstName || !lastName || !validEmail(ownerEmail)) {
          dispatch(
            appendActionMessage({ message: localeActionMessages['invalidOrganizationData'], type: SnackType.ERROR }),
          );
          return;
        }
      }
    }

    // Check for short description when creating with simplified creation (not editing)
    if (
      !editing &&
      useSimplifiedCreation &&
      (!organizationDescription || organizationDescription.trim().length < MINIMUM_DESCRIPTION_LENGTH)
    ) {
      setShowDescriptionWarning(true);
      return;
    }

    proceedWithCreation();
  };

  const handleSkipDescription = () => {
    setShowDescriptionWarning(false);
    proceedWithCreation();
  };

  const handleGoBackToEdit = () => {
    setShowDescriptionWarning(false);
  };

  const getEncouragingMessage = () => {
    if (!useSimplifiedCreation || editing) return null;

    const length = organizationDescription?.trim().length || 0;

    if (length === 0) return null;
    if (length < 50) return { text: localeOrganizations['descriptionEncouragement1'], color: 'text-red-500' };
    if (length < 100) return { text: localeOrganizations['descriptionEncouragement2'], color: 'text-orange-500' };
    if (length < 150) return { text: localeOrganizations['descriptionEncouragement3'], color: 'text-yellow-600' };
    if (length < 200) return { text: localeOrganizations['descriptionEncouragement4'], color: 'text-yellow-600' };
    if (length < 300) return { text: localeOrganizations['descriptionEncouragement5'], color: 'text-green-600' };
    return { text: localeOrganizations['descriptionEncouragement5'], color: 'text-green-600' };
  };

  if (showDescriptionWarning) {
    return (
      <Box>
        <Typography variant="h6" className="mb-4 text-center">
          {localeOrganizations['shortDescriptionWarning']}
        </Typography>
        <Typography variant="body14" className="mb-6">
          {localeOrganizations['shortDescriptionMessage']}
        </Typography>
        <Box className="flex justify-between gap-4">
          <WolfButton onClick={handleGoBackToEdit} color="primary" className="flex-1">
            {localeOrganizations['goBackAndAddMore']}
          </WolfButton>
          <WolfButton onClick={handleSkipDescription} color="secondary" className="flex-1">
            {localeOrganizations['skipDescription']}
          </WolfButton>
        </Box>
      </Box>
    );
  }

  const encouragingMessage = getEncouragingMessage();

  return (
    <Box>
      <Box className={editing ? 'min-h-32' : ''}>
        <WolfInput
          fullWidth
          autoComplete="organization"
          label={localeCommon['organization'] + ' ' + localeCommon['name']}
          placeholder={localeCommon['organization'] + ' ' + localeCommon['name']}
          onChange={handleNameChange}
          value={organizationName}
        />
      </Box>
      <Box className={editing ? 'min-h-32' : ''}>
        <WolfInput
          fullWidth
          autoComplete="description"
          label={localeCommon['organization'] + ' ' + localeCommon['description']}
          placeholder={localeCommon['organization'] + ' ' + localeCommon['description']}
          onChange={handleDescriptionChange}
          value={organizationDescription}
        />
        {encouragingMessage && (
          <Typography variant="body12" className={`mt-1 ${encouragingMessage.color}`}>
            {encouragingMessage.text}
          </Typography>
        )}
      </Box>

      {!editing && (
        <Box className="flex items-center gap-1 my-2">
          <FormControlLabel
            control={<Checkbox checked={seedData} onChange={(e) => setSeedData(e.target.checked)} color="primary" />}
            label={localeOrganizations['seedOrganizationData']}
          />
          <WolfTooltip title={localeOrganizations['seedOrganizationDataTooltip']} placement="right">
            <InfoOutlinedIcon fontSize="small" className="text-gray-500 cursor-help" />
          </WolfTooltip>
        </Box>
      )}

      {!editing && !hideOwnerEmail && (
        <>
          {/* For super-admin (non-simplified creation), show editable owner email field */}
          <Box>
            <WolfInput
              fullWidth
              autoComplete="email"
              label={localeCommon['owner'] + ' ' + localeCommon['email']}
              placeholder={localeCommon['organization'] + ' ' + localeCommon['email']}
              onChange={handleEmailChange}
              value={ownerEmail}
              type="email"
              required
            />
          </Box>
          {/* Only show firstName and lastName when not using simplified creation */}
          {!useSimplifiedCreation && (
            <>
              <Box>
                <WolfInput
                  fullWidth
                  autoComplete="given-name"
                  label={localeCommon['owner'] + ' ' + localeUsers['firstName']}
                  placeholder={localeCommon['user'] + ' ' + localeUsers['firstName']}
                  onChange={handleFirstNameChange}
                  value={firstName}
                />
              </Box>
              <Box>
                <WolfInput
                  fullWidth
                  autoComplete="family-name"
                  label={localeCommon['owner'] + ' ' + localeUsers['lastName']}
                  placeholder={localeCommon['user'] + ' ' + localeUsers['lastName']}
                  onChange={handleLastNameChange}
                  value={lastName}
                />
              </Box>
            </>
          )}
        </>
      )}
      <Box className="flex justify-between my-4 ">
        <WolfButton onClick={modalClose} color="secondary">
          {localeCommon['cancel']}
        </WolfButton>
        <WolfButton onClick={submit} color="primary" autoFocus disabled={saving}>
          {editing ? localeCommon['edit'] : localeCommon['create']}
        </WolfButton>
      </Box>
    </Box>
  );
};

export default CreateEditOrganizationModal;
